import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useAuth } from '@/lib/contexts/AuthContext'
import { acceptAccountInvite, declineAccountInvite, getIncomingInvites } from '@/lib/api/api'
import {
  SystemCardShell,
  SystemCardHeading,
  SystemCardActions,
  SystemCardPrimary,
  SystemCardSecondary,
  SystemCardProgress,
  SystemCardFooter,
} from '@/components/shared/layout'

interface InviteDetails {
  inviterName: string
  inviterEmail: string
  accountName: string
  propertyCount: number
  properties: string[]
  isValid: boolean
}

export default function AcceptInvite() {
  const { token: inviteToken } = useParams()
  const navigate = useNavigate()
  const { token: authToken } = useAuth()
  const [isLoading, setIsLoading] = useState(true)
  const [isProcessing, setIsProcessing] = useState(false)
  const [inviteDetails, setInviteDetails] = useState<InviteDetails | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<'joined' | 'declined' | null>(null)

  useEffect(() => {
    if (!inviteToken) {
      setError('This invitation link is incomplete.')
      setIsLoading(false)
      return
    }

    // If user is not authenticated, redirect to sign-in with return URL
    if (!authToken) {
      navigate(`/sign-in?redirect=/accept-invite/${inviteToken}`)
      return
    }

    // Validate the invite token and get details
    validateInvite()
  }, [inviteToken, authToken, navigate])

  const validateInvite = async () => {
    if (!inviteToken) return

    try {
      setIsLoading(true)
      // The signed-in user's pending invites; this link's token must be one of them
      const invites: Array<Omit<InviteDetails, 'isValid'> & { token?: string }> =
        await getIncomingInvites(authToken!)
      const invite = Array.isArray(invites) ? invites.find((i) => i.token === inviteToken) : undefined
      if (!invite) {
        setError('This invitation is no longer valid, or it was sent to a different email address.')
        return
      }
      setInviteDetails({
        inviterName: invite.inviterName,
        inviterEmail: invite.inviterEmail,
        accountName: invite.accountName,
        propertyCount: invite.propertyCount ?? invite.properties?.length ?? 0,
        properties: invite.properties ?? [],
        isValid: true,
      })
    } catch (error) {
      setError("We couldn't check this invitation. Refresh to try again.")
    } finally {
      setIsLoading(false)
    }
  }

  const handleAcceptInvite = async () => {
    if (!inviteToken || !authToken) return

    try {
      setIsProcessing(true)
      setError(null)
      
      await acceptAccountInvite(inviteToken, authToken)
      
      setSuccess('joined')
      
      // Redirect to dashboard after a brief delay
      setTimeout(() => {
        navigate('/')
      }, 2000)
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to accept invitation'
      setError(message)
    } finally {
      setIsProcessing(false)
    }
  }

  const handleDeclineInvite = async () => {
    if (!inviteToken || !authToken) return

    try {
      setIsProcessing(true)
      setError(null)
      
      await declineAccountInvite(inviteToken, authToken)
      
      setSuccess('declined')
      
      // Redirect after a brief delay
      setTimeout(() => {
        navigate('/')
      }, 2000)
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to decline invitation'
      setError(message)
    } finally {
      setIsProcessing(false)
    }
  }

  if (isLoading) {
    return (
      <SystemCardShell>
        <SystemCardHeading eyebrow="Team invitation" title="Checking your invitation" />
        <SystemCardProgress label="Validating" />
      </SystemCardShell>
    )
  }

  if (success) {
    return (
      <SystemCardShell>
        <SystemCardHeading
          success={success === 'joined'}
          eyebrow="Team invitation"
          title={success === 'joined' ? `You joined ${inviteDetails?.accountName ?? 'the team'}` : 'Invitation declined'}
        >
          Taking you to the dashboard.
        </SystemCardHeading>
      </SystemCardShell>
    )
  }

  if (!inviteDetails?.isValid) {
    return (
      <SystemCardShell>
        <SystemCardHeading eyebrow="Team invitation" title="This invitation isn't valid">
          {error || 'This invitation link is no longer valid or has expired.'}
        </SystemCardHeading>
        <SystemCardSecondary asChild>
          <Link to="/">Return to dashboard</Link>
        </SystemCardSecondary>
      </SystemCardShell>
    )
  }

  const { inviterName, inviterEmail, accountName, propertyCount, properties } = inviteDetails

  return (
    <SystemCardShell>
      <SystemCardHeading eyebrow="Team invitation" title={`Join ${accountName}`}>
        {inviterName} ({inviterEmail}) has invited you to join {accountName}.
      </SystemCardHeading>

      <div className="flex flex-col gap-1 rounded-lg bg-canvas px-3.5 py-3">
        <p className="text-[13px] font-medium leading-5 text-foreground">
          You'll have access to {propertyCount} {propertyCount === 1 ? 'property' : 'properties'}
        </p>
        {properties.slice(0, 3).map((property) => (
          <p key={property} className="text-xs leading-[18px] text-muted-foreground">{property}</p>
        ))}
        {properties.length > 3 && (
          <p className="text-xs leading-[18px] text-muted-foreground">+ {properties.length - 3} more</p>
        )}
      </div>

      {error && <p role="alert" className="-mt-3 text-[13px] leading-5 text-destructive">{error}</p>}

      <SystemCardActions>
        <SystemCardPrimary onClick={handleAcceptInvite} disabled={isProcessing}>
          Accept invitation
        </SystemCardPrimary>
        <SystemCardSecondary onClick={handleDeclineInvite} disabled={isProcessing}>
          Decline
        </SystemCardSecondary>
      </SystemCardActions>

      <SystemCardFooter className="flex justify-center">
        <Link
          to="/"
          className="text-[13px] font-medium leading-5 text-foreground underline underline-offset-2 transition-colors hover:text-muted-foreground"
        >
          Return to dashboard
        </Link>
      </SystemCardFooter>
    </SystemCardShell>
  )
}
